import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Stack, Tab, Tabs, type Theme, Typography, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import { useHybrids } from '../hybrids/use-hybrids';
import AddHybridModal from './add-hybrid-modal';
import CardCarousel from './card-carousel';
import DensityCurve from './density-curve';
import FertilizationTab from './fertilization-tab';
import HybridDensityCard from './hybrid-density-card';
import NumberModal from './number-modal';
import PhenologyTab from './phenology-tab';
import { useManagementData } from './use-management-data';

type ManagementTab = 'density' | 'phenology' | 'fertilization';

/**
 * "Manejo recomendado" — the wizard's last step: density, phenology and fertilization for the
 * hybrids chosen on the previous step. The recommendation itself is saved by the view that owns
 * the wizard, on Terminar.
 */
function ManagementStep({
    field,
    crop = 'corn',
    onEditEnvironment,
}: {
    field: Field;
    crop?: 'corn' | 'soy';
    /** Applying a new planting date invalidates the environment, so the wizard steps back. */
    onEditEnvironment: () => void;
}) {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);

    const [tab, setTab] = useState<ManagementTab>('density');
    const [achievementOpen, setAchievementOpen] = useState(false);
    const [areaOpen, setAreaOpen] = useState(false);
    const [addHybridOpen, setAddHybridOpen] = useState(false);

    const { hybrids, planterEfficiency, areaInHectares, isLoading, isLoadingFertilization } = useManagementData({
        field,
        crop,
    });
    const { featuredProducts, otherProducts } = useHybrids({ field, crop });

    const selectedIds: number[] = cropConfig?.products || [];
    const maxSelection = settings.maxProductSelection;
    const emptySlots = Math.max(0, maxSelection - selectedIds.length);
    const candidates = [...featuredProducts, ...otherProducts].filter((hybrid) => !selectedIds.includes(hybrid.id));

    const updateCropConfig = (key: string, value: unknown) =>
        dispatch(updateActiveRecommendationCropConfig({ key, value }));

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    const cards = [
        ...hybrids.map((hybrid) => (
            <HybridDensityCard
                key={hybrid.id}
                hybrid={hybrid}
                planterEfficiency={planterEfficiency}
                areaInHectares={areaInHectares}
            />
        )),
        ...Array.from({ length: emptySlots }).map((_slot, index) => (
            <Stack
                // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
                key={`empty-${index}`}
                alignItems='center'
                justifyContent='center'
                sx={{
                    minHeight: 160,
                    height: '100%',
                    borderRadius: '4px',
                    border: '1px dashed',
                    borderColor: (theme) => theme.palette.divider,
                }}
            >
                <Button
                    variant='text'
                    color='primary'
                    startIcon={<AddIcon />}
                    onClick={() => setAddHybridOpen(true)}
                    disabled={candidates.length === 0}
                >
                    {_('Agregar un híbrido')}
                </Button>
            </Stack>
        )),
    ];

    return (
        <Stack spacing={3} pb={4}>
            <Tabs
                value={tab}
                onChange={(_event, value) => setTab(value)}
                variant='fullWidth'
                textColor='secondary'
                indicatorColor='secondary'
            >
                <Tab value='density' label={_('Densidad')} />
                <Tab value='phenology' label={_('Fenología')} />
                <Tab value='fertilization' label={_('Fertilización')} />
            </Tabs>

            {tab === 'phenology' ? (
                <PhenologyTab
                    field={field}
                    crop={crop}
                    plantingDate={cropConfig?.plantingDate}
                    compact={isMobile}
                    onPlantingDateApplied={onEditEnvironment}
                />
            ) : tab === 'fertilization' ? (
                <FertilizationTab hybrids={hybrids} isLoading={isLoadingFertilization} />
            ) : (
                <Stack spacing={3}>
                    <Stack>
                        <Typography variant='h6' fontWeight={700} color='primary'>
                            {_('Densidad objetivo')}
                        </Typography>
                        <Typography variant='body2' color='primary'>
                            {_(
                                'Cantidad mínima de plantas a cosecha para el máximo rendimiento en el ambiente definido.'
                            )}
                        </Typography>
                    </Stack>

                    {isLoading && !hybrids.length ? (
                        <Stack alignItems='center' py={6}>
                            <Spinner />
                        </Stack>
                    ) : (
                        <DensityCurve hybrids={hybrids} height={isMobile ? 220 : 320} />
                    )}

                    {/* Too wide to stack on a phone. */}
                    {isMobile ? (
                        <CardCarousel>{cards}</CardCarousel>
                    ) : (
                        <Box
                            display='grid'
                            gap={2}
                            gridTemplateColumns={{ sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                            alignItems='stretch'
                        >
                            {cards}
                        </Box>
                    )}

                    <Stack
                        direction={isMobile ? 'column' : 'row'}
                        spacing={1}
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        justifyContent='space-between'
                        p={2}
                        borderRadius='4px'
                        sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}
                    >
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <InfoOutlinedIcon sx={{ fontSize: 18, color: (t) => t.palette.primary.main }} />
                            <Typography variant='body2' color='primary'>
                                {_('Densidad de siembra calculada con un porcentaje de logro del {{value}}%.', {
                                    value: planterEfficiency,
                                })}
                            </Typography>
                        </Stack>
                        <Stack direction='row' spacing={2}>
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ textDecoration: 'underline', p: 0 }}
                                onClick={() => setAchievementOpen(true)}
                            >
                                {_('Cambiar porcentaje')}
                            </Button>
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ textDecoration: 'underline', p: 0 }}
                                onClick={() => setAreaOpen(true)}
                            >
                                {_('Cambiar superficie')}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            )}

            <NumberModal
                open={achievementOpen}
                onClose={() => setAchievementOpen(false)}
                onApply={(value) => {
                    updateCropConfig('userPlanterEfficiency', value);
                    setAchievementOpen(false);
                }}
                title={_('Cambiar el porcentaje de logro')}
                description={_('Ingresá el porcentaje de plantas que suelen nacer con éxito en el lote.')}
                value={planterEfficiency}
                suffix='%'
                min={1}
                max={100}
                rangeError={_('El porcentaje debe ser de 1 a 100%')}
            />

            <NumberModal
                open={areaOpen}
                onClose={() => setAreaOpen(false)}
                onApply={(value) => {
                    updateCropConfig('userArea', value);
                    setAreaOpen(false);
                }}
                title={_('Cambiar la superficie del lote')}
                description={_('Ingresá la superficie a sembrar para calcular las bolsas totales.')}
                value={areaInHectares !== undefined ? Math.round(areaInHectares * 100) / 100 : undefined}
                min={1}
                max={100000}
                rangeError={_('Ingresá una superficie válida.')}
            />

            <AddHybridModal
                open={addHybridOpen}
                onClose={() => setAddHybridOpen(false)}
                onAdd={(id) => updateCropConfig('products', [...selectedIds, id])}
                candidates={candidates}
            />
        </Stack>
    );
}

export default ManagementStep;
